import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Navigate, useNavigate } from "react-router-dom";

import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2, ShieldCheck } from "lucide-react";

import { AuthLayout } from "@/app/layouts/AuthLayout";
import { type TotpCodeInput, TotpCodeSchema } from "@/features/auth/domain/credentials.schema";
import { AuthErrorAlert } from "@/features/auth/presentation/components/AuthErrorAlert";
import { AuthHeading } from "@/features/auth/presentation/components/AuthHeading";
import { CopyableSecret } from "@/features/auth/presentation/components/CopyableSecret";
import { OtpInput, type OtpInputHandle } from "@/features/auth/presentation/components/OtpInput";
import { Button } from "@/shared/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/shared/components/ui/form";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { cn } from "@/shared/lib/utils";
import { useAppDispatch, useAppSelector } from "@/store";
import { useMfaEnrollMutation, useMfaVerifyMutation } from "@/store/apis/adminApi.enhanced";
import type { TotpEnrollment } from "@/store/apis/adminApi.types";
import { selectIsAal1, selectIsAuthenticated, selectMfa } from "@/store/selectors";
import { clearSession, setSession } from "@/store/slices/authSlice";

/**
 * MFA — Paso 2 del flow. aal1 → verificar TOTP → aal2 → dashboard.
 *
 * Dos sub-flows según el estado del back:
 *   - `enroll_required` (primer login): auto-enroll, muestra QR + secret
 *     copyable + instrucciones.
 *   - `challenge_required` (recurrente): solo pide el código.
 *
 * UX premium:
 *   - OTP segmentado con auto-advance, paste, auto-submit al completar 6.
 *   - Shake visual en código inválido.
 *   - Success pulse pre-redirect para dar closure táctil.
 *   - Botón "Volver" (clear session) separado del submit por hierarchy.
 */
export function MfaPage() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const mfa = useAppSelector(selectMfa);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isAal1 = useAppSelector(selectIsAal1);

  const [enrollment, setEnrollment] = useState<TotpEnrollment | null>(null);
  const [success, setSuccess] = useState(false);
  const [enroll, enrollState] = useMfaEnrollMutation();
  const [verify, verifyState] = useMfaVerifyMutation();
  const otpRef = useRef<OtpInputHandle | null>(null);

  const form = useForm<TotpCodeInput>({
    resolver: zodResolver(TotpCodeSchema),
    defaultValues: { code: "" },
    mode: "onSubmit",
  });

  const needsEnrollment = mfa?.status === "enroll_required";

  // Auto-enroll al llegar a la pantalla si corresponde.
  useEffect(() => {
    if (!needsEnrollment || enrollment) return;
    void enroll()
      .unwrap()
      .then(setEnrollment)
      .catch(() => {
        // El error se muestra via AuthErrorAlert leyendo enrollState.
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsEnrollment, enrollment]);

  // Limpia OTP cuando llega error del back (código inválido).
  useEffect(() => {
    if (verifyState.isError) {
      otpRef.current?.clear();
    }
  }, [verifyState.isError]);

  if (isAuthenticated && !success) return <Navigate to="/" replace />;
  if (!isAal1 && !success) return <Navigate to="/login" replace />;

  const factorId = mfa?.status === "challenge_required" ? mfa.factor_id : enrollment?.factor_id;

  const submitCode = async (code: string) => {
    if (!factorId) return;
    try {
      const session = await verify({
        body: { factor_id: factorId, code },
      }).unwrap();
      setSuccess(true);
      dispatch(setSession(session));
      // Pequeño delay para que la pulse-success sea perceptible.
      window.setTimeout(() => navigate("/", { replace: true }), 600);
    } catch {
      // AuthErrorAlert consume verifyState.error.
    }
  };

  const onSubmit = form.handleSubmit(async (values) => {
    await submitCode(values.code);
  });

  const cancel = () => {
    dispatch(clearSession());
    navigate("/login", { replace: true });
  };

  return (
    <AuthLayout
      visualTitle={
        needsEnrollment ? "Un paso más para proteger tu cuenta." : "Verifiquemos que eres tú."
      }
      visualSubtitle="Usamos verificación en dos pasos para proteger la información sensible de las usuarias del app."
    >
      <AuthHeading
        kicker="Paso 2 de 2"
        icon={<ShieldCheck className="h-5 w-5" />}
        title={needsEnrollment ? "Configura tu app autenticadora" : "Verificación en dos pasos"}
        subtitle={
          needsEnrollment
            ? "Escanea el QR con Google Authenticator, 1Password, Authy o similar. Este paso solo se hace una vez."
            : "Abre tu app autenticadora e ingresa el código de 6 dígitos que ves ahora."
        }
      />

      {/* Bloque enroll — QR + secret copyable + pasos. */}
      {needsEnrollment ? (
        <div className="mb-7 overflow-hidden rounded-2xl border bg-card shadow-card-premium">
          <div className="grid gap-5 p-5 sm:grid-cols-[auto_1fr] sm:gap-6 sm:p-6">
            {/* QR */}
            <div className="flex items-start justify-center">
              {enrollState.isLoading || !enrollment ? (
                <Skeleton className="h-36 w-36 rounded-xl" />
              ) : (
                <img
                  src={enrollment.qr_code}
                  alt="Código QR para configurar tu autenticador"
                  className="h-36 w-36 rounded-xl bg-white p-2.5 ring-1 ring-border"
                />
              )}
            </div>

            {/* Steps + manual code */}
            <div className="min-w-0 space-y-3.5">
              <ol className="space-y-2 text-[13px] text-foreground">
                <Step n={1}>Abre tu app autenticadora.</Step>
                <Step n={2}>Escanea el QR o ingresa el código manual.</Step>
                <Step n={3}>Ingresa el código de 6 dígitos abajo.</Step>
              </ol>

              {enrollment ? (
                <CopyableSecret value={enrollment.secret} />
              ) : (
                <Skeleton className="h-9 w-full rounded-lg" />
              )}
            </div>
          </div>
        </div>
      ) : null}

      {/* Error del enroll (si falló el POST /mfa/enroll) */}
      <AuthErrorAlert error={enrollState.error} fallback="No se pudo generar el código QR." />

      <Form {...form}>
        <form onSubmit={onSubmit} className="space-y-5" noValidate>
          <FormField
            control={form.control}
            name="code"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormControl>
                  <OtpInput
                    ref={otpRef}
                    value={field.value}
                    onChange={field.onChange}
                    onComplete={(code) => void submitCode(code)}
                    invalid={verifyState.isError || fieldState.invalid}
                    disabled={verifyState.isLoading || success}
                    // La usuaria llega a esta pantalla con la app
                    // autenticadora abierta y el OTP es su único input.
                    // Auto-focus mejora UX más que lo que perjudica a11y.
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus
                    aria-label="Código de 6 dígitos"
                  />
                </FormControl>
                <FormMessage className="mt-3" />
              </FormItem>
            )}
          />

          <AuthErrorAlert
            error={verifyState.error}
            fallback="Código inválido. Intenta con el actual."
          />

          <div className="flex gap-3 pt-1">
            <Button
              type="button"
              variant="ghost"
              onClick={cancel}
              disabled={verifyState.isLoading || success}
              className="h-12 flex-none px-4 text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              Volver
            </Button>
            <Button
              type="submit"
              disabled={verifyState.isLoading || !factorId || success}
              className={cn(
                "h-12 flex-1 text-[14px] font-medium shadow-sm transition-all hover:shadow-md",
                success && "bg-primary/90",
              )}
            >
              {success ? (
                <>
                  <CheckCircle2 className="h-4 w-4 animate-pulse-success" />
                  Verificado
                </>
              ) : verifyState.isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Verificando…
                </>
              ) : (
                <>
                  Verificar
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        </form>
      </Form>

      {/* Hint final */}
      <div className="mt-8 rounded-xl bg-muted/50 p-4">
        <p className="text-[12px] leading-relaxed text-muted-foreground">
          <span className="font-medium text-foreground">Tip:</span> el código cambia cada 30
          segundos. Si el actual no funciona, espera unos segundos e intenta con el siguiente.
        </p>
      </div>
    </AuthLayout>
  );
}

/** Paso enumerado del enroll — contador mono + body sans. */
function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-3">
      <span
        aria-hidden
        className="mt-[1px] inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 font-mono text-[11px] font-medium text-primary"
      >
        {n}
      </span>
      <span>{children}</span>
    </li>
  );
}
